import { Link } from "react-router-dom";
import styled from "styled-components";

const scrollToSection = (targetId: string) => (
  event: React.MouseEvent<HTMLAnchorElement>
) => {
  event.preventDefault();
  const targetElement = document.getElementById(targetId);
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

type FooterLinkItem =
  | { label: string; to: string }
  | { label: string; href: string; onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void };

const FOOTER_COLUMNS: { title: string; links: FooterLinkItem[] }[] = [
  {
    title: "Majura",
    links: [
      { label: "Our Services", href: "#stat-section", onClick: scrollToSection("stat-section") },
      { label: "Our Mission", href: "#explore-more-section", onClick: scrollToSection("explore-more-section") }
    ]
  },
  {
    title: "Get Started",
    links: [
      { label: "Book a Demo", href: "#contact-section", onClick: scrollToSection("contact-section") },
      { label: "Careers", to: "/careers" }
    ]
  },
  {
    title: "Company",
    links: [{ label: "About Us", to: "/about-us" }]
  }
];

const Footer = () => {
  return (
    <FooterWrapper>
      <FooterTop>
        <LogoColumn>
          <Link to="/">
            <Logo src="assets/img/logo.png" alt="Majura" />
          </Link>

          <Address>
            PT MAJURA DIGITATAL SOLUSI
            <br />
            Jalan Kali Besar Barat At Nomor 50, Desa/Kelurahan Roa Malaka,
            <br />
            Kec. Tambora, Kota Adm. Jakarta Barat,
            <br />
            Provinsi DKI Jakarta, Kode Pos: 11230
          </Address>
        </LogoColumn>

        {FOOTER_COLUMNS.map((column) => (
          <LinkColumn key={column.title}>
            <ColumnTitle>{column.title}</ColumnTitle>
            {column.links.map((link) =>
              "to" in link ? (
                <FooterLink key={link.label} to={link.to}>
                  {link.label}
                </FooterLink>
              ) : (
                <FooterAnchor key={link.label} href={link.href} onClick={link.onClick}>
                  {link.label}
                </FooterAnchor>
              )
            )}
          </LinkColumn>
        ))}
      </FooterTop>

      <Divider />

      <BottomBar>
        <span>© Majura Apps. All rights reserved</span>
      </BottomBar>
    </FooterWrapper>
  );
};

const FooterWrapper = styled.footer`
  width: 100%;
  background: #060a1f;
  padding: 64px 48px 24px;

  @media (max-width: 767px) {
    padding: 48px 24px 24px;
  }
`;

const FooterTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const LogoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 320px;
`;

const Logo = styled.img`
  height: auto;
  max-width: 200px;
`;

const Address = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  line-height: 1.6;
`;

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 140px;
`;

const ColumnTitle = styled.p`
  margin: 0 0 4px;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.95rem;
`;

const linkStyles = `
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
  width: fit-content;

  &:hover {
    color: #ffffff;
  }
`;

const FooterLink = styled(Link)`
  ${linkStyles}
`;

const FooterAnchor = styled.a`
  ${linkStyles}
`;

const Divider = styled.div`
  max-width: 1200px;
  margin: 48px auto 24px;
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
`;

const BottomBar = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.75rem;
`;

export default Footer;
